import React, { useEffect, useRef, useState } from "react";
import { Field } from "react-final-form";
import PlayOutlineIcon from "@rsuite/icons/PlayOutline";
import PauseOutlineIcon from "@rsuite/icons/PauseOutline";
import CloseIcon from "@rsuite/icons/Close";
import { useTranslation } from "react-i18next";
import { getBasename, isValidURL } from "../../../utils/helpers";
import AudioPlayer from "./AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";
import { Button } from "rsuite";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  maxUploadSizeInMB,
  messageTypeArr,
} from "../../../core/config/AppConstants";

interface MediaFileUploadProps {
  name?: string;
  accept?: string;
  label?: string;
  browseLabel?: string;
  mediaUrl?: string;
  mediaFile?: any;
  setMediaFile?: Function;
  maxUploadSize?: number;
  errorMessage?: string;
  mediaType?: string;
  mediaImageUrl?: string;
  onMediaTypeChange?: (mediaType: string) => void;
}

const MediaFileUpload: React.FC<MediaFileUploadProps> = ({
  name = "_song_file",
  accept = "audio/*,video/mp4",
  label = "UPLOAD MEDIA FILE",
  browseLabel = "BROWSE",
  mediaUrl = null,
  mediaFile = null,
  setMediaFile = null,
  maxUploadSize = maxUploadSizeInMB,
  errorMessage = "",
  mediaType = "audio",
  mediaImageUrl = "",
  onMediaTypeChange,
}) => {
  const { setPlayingMediaInfo, isPlaying, setIsPlaying } = useMediaContext();
  const [mediaPlayer, setMediaPlayer] = useState<HTMLMediaElement>(null);
  const [playFileName, setPlayFileName] = useState<string>("");
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [mediaInfo, setMediaInfo] = useState<any>({});
  const { t } = useTranslation();

  const [playerMediaFile, setPlayerMediaFile] = useState<any>(null);
  const [showPlayer, setShowPlayer] = useState<boolean>(false);

  const fileUpload = useRef<HTMLInputElement>(null);

  const fileUploadSize = maxUploadSize * 1024 * 1024;

  const getMediaType = (fileOrUrl: any) => {
    const type = fileOrUrl?.type?.toLowerCase?.() || "";
    const name = fileOrUrl?.name?.toLowerCase?.() || "";
    const url = typeof fileOrUrl === "string" ? fileOrUrl.toLowerCase() : "";

    if (
      type.startsWith("video/") ||
      name.endsWith(".mp4") ||
      url.includes(".mp4")
    ) {
      return "video";
    }

    if (
      type.startsWith("audio/") ||
      name.endsWith(".mp3") ||
      name.endsWith(".mpeg") ||
      name.endsWith(".wav") ||
      name.endsWith(".m4a") ||
      url.includes(".mp3") ||
      url.includes(".mpeg") ||
      url.includes(".wav") ||
      url.includes(".m4a")
    ) {
      return "audio";
    }

    if (mediaType === "video") {
      return "video";
    }

    return "audio";
  };

  const createMediaElement = (url: string) => {
    return getMediaType(url) === "video" ? document.createElement("video") : new Audio(url);
  };

  const createPlayer = (playFile: any) => {
    setMediaPlayer(playFile);
    setIsPlaying(false); // Reset playing state when a new file is selected

    // Set duration when the metadata is loaded
    playFile.addEventListener("loadedmetadata", () => {
      setDuration(playFile.duration);
    });

    // Update current time as the audio plays
    playFile.addEventListener("timeupdate", () => {
      setCurrentTime(playFile.currentTime);
    });
  };

  useEffect(() => {
    initPlayer();

    return () => {
      setIsPlaying(false);
      setPlayingMediaInfo({});
    };
  }, []);

  async function initPlayer() {
    try {
      if (mediaUrl && isValidURL(mediaUrl)) {
        const media = createMediaElement(mediaUrl);
        media.src = mediaUrl;
        createPlayer(media);
        const baseName = getBasename(mediaUrl);
        if (baseName) {
          setPlayFileName(baseName);
        }
        setPlayerMediaFile(media);
      } else {
        setMediaPlayer(null);
        setIsPlaying(false);
        setPlayingMediaInfo({});
        setShowPlayer(false);
        setCurrentTime(0);
        setPlayFileName(undefined);
        setCurrentTime(0);
        setDuration(0);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const startPlay = async () => {
    if (playerMediaFile.size > 0) {
      const mediaUrl = URL.createObjectURL(playerMediaFile);
      const selectedMediaType = getMediaType(playerMediaFile);

      setShowPlayer(true);
      setIsPlaying(true);

      setPlayingMediaInfo({
        media_url: mediaUrl,
        title: playerMediaFile?.name,
        media_type: selectedMediaType,
        media_image_url: selectedMediaType === "video" ? mediaImageUrl : "",
      });
    } else if (mediaUrl && isValidURL(mediaUrl)) {
      const media = createMediaElement(mediaUrl);
      const baseName = getBasename(mediaUrl);
      const selectedMediaType = getMediaType(mediaUrl);
      setShowPlayer(true);
      setIsPlaying(true);
      setPlayingMediaInfo({
        media_url: mediaUrl,
        title: baseName,
        media_type: selectedMediaType,
        media_image_url: selectedMediaType === "video" ? mediaImageUrl : "",
      });
      setPlayerMediaFile(media);
    }
  };

  const resetFile = async (e: any) => {
    e.preventDefault();

    if (fileUpload.current && fileUpload.current instanceof HTMLInputElement) {
      setTimeout(() => {
        fileUpload.current.value = "";
      }, 0);
    }

    setPlayerMediaFile(null);
    setPlayFileName("");
    setShowPlayer(false);
    setIsPlaying(false);
    setPlayingMediaInfo({});
    setMediaFile && setMediaFile(null);
  };

  return (
    <Field name={name}>
      {({ input, meta }) => (
        <div className="flex-item">
          <div
            className="d-flex items-center song-file align-bottom"
            style={{
              minHeight: "40px",
              display: "flex",
              alignItems: "bottom",
            }}
          >
            {showPlayer && <AudioPlayer />}
            {playerMediaFile &&
              (isPlaying ? (
                <div className="barsAnim">
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                  <div className="bar"></div>
                </div>
              ) : (
                <Button appearance="subtle" onClick={() => startPlay()}>
                  <PlayOutlineIcon />
                </Button>
              ))}
            <input
              id="file-upload"
              ref={fileUpload}
              type="file"
              accept={accept}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onChangeCapture={(e: any) => {
                setPlayerMediaFile(null);
                setIsPlaying(false);
                setPlayingMediaInfo({});
                setShowPlayer(false);
                const file = e.target.files[0];
                if (!file) {
                  setMediaFile && setMediaFile(null);
                  input.onChange(null);
                  return;
                }
                if (file && file.size > fileUploadSize) {
                  UtilityServices.showToaster({
                    message: t("FILE_SIZE_LESS_THAN", {
                      field: t("MEDIA"),
                      value: maxUploadSize,
                    }),
                    type: messageTypeArr.error,
                  });
                  e.target.value = "";
                  setMediaFile && setMediaFile(null);
                  input.onChange(null);
                  return;
                }
                setMediaFile && setMediaFile(file);
                input.onChange(file);
                if (file.size > 0) {
                  onMediaTypeChange && onMediaTypeChange(getMediaType(file));
                  setPlayerMediaFile(file);
                  setPlayFileName(file.name);
                } else {
                  setPlayFileName("");
                  setPlayerMediaFile("");
                }
              }}
              style={{ display: "none" }}
            />
            <label className="song-file-upload-lbl me-2" htmlFor="file-upload">
              {!playFileName ? t(label) : playFileName}
            </label>
            <br />
            {playFileName && (
              <Button
                appearance="link"
                color="red"
                onClick={(e: any) => resetFile(e)}
              >
                <CloseIcon />
              </Button>
            )}
            <Button
              className="song-file-upload-lbl mb-4"
              appearance="ghost"
              onClick={() => {
                if (fileUpload.current) {
                  fileUpload.current.click();
                }
              }}
            >
              {" " + t(browseLabel) + " "}
            </Button>
          </div>
          {(errorMessage || (meta.error && meta.touched)) && (
            <span className="error-message">{errorMessage || meta.error}</span>
          )}
        </div>
      )}
    </Field>
  );
};

export default MediaFileUpload;
